import React, { useEffect, useRef, useState } from 'react';
import { AuthUser } from '../lib/auth';

export type UserMenuProps = {
  user: AuthUser;
  onLogout: () => void | Promise<void>;
  className?: string;
};

const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout, className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 shadow-sm hover:shadow transition"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs font-semibold">
          {user.username.slice(0,1).toUpperCase()}
        </span>
        <span className="text-sm text-gray-800 max-w-[8rem] truncate">{user.username}</span>
        <svg className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
      </button>

      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-56 rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden z-30">
          <div className="px-3 py-3 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm font-semibold">
                {user.username.slice(0,1).toUpperCase()}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{user.name || user.username}</div>
                <div className="text-xs text-gray-500 truncate">{user.email || (user.role ? `Role: ${user.role}` : 'Signed in')}</div>
              </div>
            </div>
          </div>
          <div className="p-1">
            <a href="/quill-admin" className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-gray-700 hover:bg-gray-50" role="menuitem">
              <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l2-2 7-7 7 7 2 2"/><path d="M5 10v10a1 1 0 001 1h12a1 1 0 001-1V10"/></svg>
              Admin Dashboard
            </a>
            <a href="/profile" className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-gray-700 hover:bg-gray-50" role="menuitem">
              <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 12a5 5 0 100-10 5 5 0 000 10z"/><path d="M20 21a8 8 0 10-16 0"/></svg>
              Profile
            </a>
            <button onClick={() => { setOpen(false); onLogout(); }} className="w-full text-left flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-red-600 hover:bg-red-50" role="menuitem">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><path d="M10 17l5-5-5-5"/><path d="M15 12H3"/></svg>
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
